import { useState, useEffect, useCallback } from 'react';
import { format } from "date-fns";
import toast from 'react-hot-toast';
import {
    useTheme,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Box,
    Typography,
    IconButton,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    InputAdornment,
    Autocomplete,
    Divider,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { enGB } from 'date-fns/locale';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from "state/axios";
import FlexBetween from 'components/FlexBetween';


const WITH_DELAYS = new Set(['Departure', 'Delay', 'DelayedTakeOff', 'ReturnToRamp', 'Diversion']);
const WITH_PAX    = new Set(['Departure', 'Diversion']);

const MovementMessageDialog = ({ 
    open, 
    onClose, 
    flightRegOptions, 
    flight,
}) => {
    const theme = useTheme();

    const [delayCodes, setDelayCodes]     = useState([]);
    const [delayCode1, setDelayCode1]     = useState('');
    const [delayCode2, setDelayCode2]     = useState('');
    const [delayDuration1, setDelayDuration1] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
    const [delayDuration2, setDelayDuration2] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
    const [classC, setClassC]     = useState(0);
    const [classY, setClassY]     = useState(flight?.noOfPax || 0);
    const [classCHD, setClassCHD] = useState(0);
    const [messageType, setMessageType] = useState('Departure');
    const [supplementaryInfo, setSupplementaryInfo] = useState('');

    const [values, setValues] = useState(() => ({
        flightActualDayDep: flight.flightActualDayDep ? new Date(flight.flightActualDayDep) : null,
        flightActualDayArr: flight.flightActualDayArr ? new Date(flight.flightActualDayArr) : null,
        flightReg:     flight.flightReg     || '',
        flightCarrier: flight.flightCarrier || '',
        flightNo:      flight.flightNo      || '',
        dep:     flight.dep     || '',
        arr:     flight.arr     || '',
        etd:     flight.etd     || '',
        eta:     flight.eta     || '',
        atd:     flight.atd     || '',
        ata:     flight.ata     || '',
        takeOff:   flight.takeOff   || '',
        touchDown: flight.touchDown || '',
    }));

    useEffect(() => {
        if (!open) return;
        api.get('/delaycodes')
            .then(r  => setDelayCodes(r.data))
            .catch(() => toast.error("Failed to fetch delay codes!"));
    }, [open]);

    const handleDateChange  = (field) => (v) => setValues(p => ({ ...p, [field]: v ? new Date(v) : null }));
    const handleTimeChange  = (field) => (t) => setValues(p => ({ ...p, [field]: t ? format(t, 'HH:mm') : '' }));
    const handleValueChange = (field) => (e) => setValues(p => ({ ...p, [field]: e.target.value }));
    const handleDelayDur    = (setter) => (t) => {
        if (!t) return;
        setter(p => { const d = new Date(p); d.setHours(t.getHours(), t.getMinutes(), 0, 0); return d; });
    };

    const toTimeObj = (date, time) => {
        if (!time || !date) return null;
        try { return new Date(`${format(date, 'yyyy-MM-dd')}T${time}:00`); }
        catch { return null; }
    };

    const buildMVTMessage = useCallback(() => {
        const fmt = (date, time) => {
            if (!time) return '0000';
            try { return format(new Date(`${format(date || new Date(), 'yyyy-MM-dd')}T${time}:00`), 'HHmm'); }
            catch { return '0000'; }
        };

        const { flightActualDayDep, flightActualDayArr, flightNo, flightCarrier = '', flightReg = '', dep = '', arr = '' } = values;
        if (!flightActualDayDep || !flightActualDayArr) return '';

        const etd       = fmt(flightActualDayDep, values.etd);
        const eta       = fmt(flightActualDayDep, values.eta);
        const atd       = fmt(flightActualDayDep, values.atd);
        const takeOff   = fmt(flightActualDayDep, values.takeOff);
        const touchDown = fmt(flightActualDayDep, values.touchDown);
        const ata       = fmt(flightActualDayArr,  values.ata);

        const dlLine = () => {
            if (!delayCode1) return '';
            const d1 = format(delayDuration1, 'HHmm');
            if (delayCode2) return `\nDL${delayCode1}/${delayCode2}/${d1}/${format(delayDuration2, 'HHmm')}`;
            return `\nDL${delayCode1}/${d1}`;
        };
        const pxLine = () => classY > 0 ? `\nPX${classC}/${classY}/${classCHD}` : '';

        const dd  = format(flightActualDayDep, 'dd');
        const da  = format(flightActualDayArr, 'dd');
        const hdr = `${flightCarrier}${flightNo}`;

        let msg = '';
        switch (messageType) {
            case 'Departure':
                msg = `MVT\n${hdr}/${dd}.${flightReg}.${dep}\nAD${atd}${takeOff ? `/${takeOff}` : ''}${takeOff ? ` EA${eta}` : ''} ${arr}${dlLine()}${pxLine()}${ata !== '0000' ? `\nEB${ata}` : ''}`;
                break;
            case 'Arrival':
                msg = `MVT\n${hdr}/${da}.${flightReg}.${arr}\nAA${touchDown}${ata ? `/${ata}` : ''}\nFLD${da}`;
                break;
            case 'Delay':
                msg = `MVT\n${hdr}/${dd}.${flightReg}.${dep}\nED${etd}${dlLine()}\nFLD${dd}`;
                break;
            case 'DelayedTakeOff':
                msg = `MVT\n${hdr}/${dd}.${flightReg}.${dep}\nAD${atd}\nEO${takeOff}${touchDown !== '0000' ? ` EA${touchDown}` : ''}${dlLine()}${ata !== '0000' ? `\nEB${ata}` : ''}\nFLD${dd}`;
                break;
            case 'ReturnToRamp':
                msg = `MVT\n${hdr}/${dd}.${flightReg}.${dep}\nAD${atd}\nRR${takeOff}${touchDown !== '0000' ? ` EA${touchDown}` : ''}${dlLine()}${ata !== '0000' ? `\nEB${ata}` : ''}\nFLD${dd}`;
                break;
            case 'ReturnFromAirborne':
                msg = `MVT\n${hdr}/${dd}.${flightReg}.${dep}\nFR${touchDown}${ata ? `/${ata}` : ''}`;
                break;
            case 'Diversion':
                msg = `DIV\n${hdr}/${da}.${flightReg}.${arr}\nEA${touchDown}${ata ? `/${ata}` : ''} ${arr}${delayCode1 ? `\nDR${delayCode1}` : ''}${pxLine()}${ata !== '0000' ? `\nEB${ata}` : ''}\nFLD${dd}`;
                break;
            default: break;
        }

        if (supplementaryInfo) msg += `\nSI ${supplementaryInfo}`;
        return msg;
    }, [values, messageType, delayCode1, delayDuration1, delayCode2, delayDuration2, classC, classY, classCHD, supplementaryInfo]);

    const handleSendMessage = async () => {
        const msg = buildMVTMessage();
        if (!msg) return toast.error('MVT message is empty');
        try {
            await api.post('/flights/send-mvt', {
                filename: `MVT_${format(new Date(), 'ddHHmm')}.txt`,
                content: msg,
            });
            toast.success('Movement message sent to SFTP successfully.');
            onClose();
        } catch (err) {
            toast.error(err?.message || 'Failed to send MVT to SFTP!');
        }
    };

    const handleCopyMessage = () => {
        const msg = buildMVTMessage();
        if (!msg) return toast.error('No message to copy.');
        navigator.clipboard.writeText(msg)
            .then(() => toast.success('Copied to clipboard.'))
            .catch(() => toast.error('Failed to copy!'));
    };

    // Conditional field visibility
    const showDelays    = WITH_DELAYS.has(messageType);
    const showPax       = WITH_PAX.has(messageType);
    const showETD       = ['Delay', 'DelayedTakeOff'].includes(messageType);
    const showETA       = messageType === 'Departure';
    const showATD       = ['Departure', 'DelayedTakeOff', 'ReturnToRamp'].includes(messageType);
    const showTakeOff   = ['Departure', 'DelayedTakeOff', 'ReturnToRamp'].includes(messageType);
    const showTouchDown = ['Arrival', 'DelayedTakeOff', 'ReturnToRamp', 'ReturnFromAirborne', 'Diversion'].includes(messageType);
    const showATA       = messageType !== 'Delay';

    const { flightActualDayDep, flightActualDayArr } = values;

    const delayAutocompleteProps = (value, onChange) => ({
        size: 'small',
        options: delayCodes,
        getOptionLabel: (o) => `${o.delayIndex} - ${o.description}`,
        value: delayCodes.find(d => d.delayIndex === value) || null,
        onChange,
        isOptionEqualToValue: (o, v) => o.delayIndex === v.delayIndex,
    });


    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
            <Dialog 
                open={open} 
                onClose={onClose} 
                maxWidth="md" 
                fullWidth
            >
                <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}`, py: 1 }}>
                    <FlexBetween>
                        <Typography fontWeight="bold" variant="subtitle1" letterSpacing={1}>
                            MOVEMENT MESSAGE
                        </Typography>
                        <IconButton 
                            size="small" 
                            onClick={onClose}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </FlexBetween>
                </DialogTitle>

                <DialogContent dividers>
                    <Box display="flex" flexDirection="column" gap={2}>

                        {/* Message Type + Aircraft */}
                        <Box 
                            display="grid" 
                            gridTemplateColumns="1fr 1fr" 
                            gap={1}
                        >
                            <FormControl fullWidth size="small">
                                <InputLabel>Message Type</InputLabel>
                                <Select 
                                    label="Message Type" 
                                    value={messageType} 
                                    onChange={(e) => setMessageType(e.target.value)}
                                >
                                    <MenuItem value="Departure">Departure</MenuItem>
                                    <MenuItem value="Arrival">Arrival</MenuItem>
                                    <MenuItem value="Delay">Delay</MenuItem>
                                    <MenuItem value="DelayedTakeOff">Delay Take-Off</MenuItem>
                                    <MenuItem value="ReturnToRamp">Return to Ramp</MenuItem>
                                    <MenuItem value="ReturnFromAirborne">Return from Airborne</MenuItem>
                                    <MenuItem value="Diversion">Diversion</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Aircraft</InputLabel>
                                <Select 
                                    label="Aircraft" 
                                    value={values.flightReg} 
                                    onChange={handleValueChange('flightReg')}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {flightRegOptions?.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Dates */}
                        <Box display="flex" gap={1}>
                            <Box flex={1}>
                                <DatePicker
                                    label="Departure Date"
                                    value={flightActualDayDep}
                                    onChange={handleDateChange('flightActualDayDep')}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </Box>
                            <Box flex={1}>
                                <DatePicker
                                    label="Arrival Date"
                                    value={flightActualDayArr}
                                    onChange={handleDateChange('flightActualDayArr')}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </Box>
                        </Box>

                        {/* Movement times (conditional) */}
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {showETD && (
                                <TimePicker 
                                    label="ETD" 
                                    views={['hours', 'minutes']} 
                                    format="HH:mm"
                                    value={toTimeObj(flightActualDayDep, values.etd)}
                                    onChange={handleTimeChange('etd')}
                                    slotProps={{ textField: { size: 'small', sx: { flex: '1 1 130px' } } }}
                                />
                            )}
                            {showATD && (
                                <TimePicker 
                                    label="Off-Block" 
                                    views={['hours', 'minutes']} 
                                    format="HH:mm"
                                    value={toTimeObj(flightActualDayDep, values.atd)}
                                    onChange={handleTimeChange('atd')}
                                    slotProps={{ textField: { size: 'small', sx: { flex: '1 1 130px' } } }}
                                />
                            )}
                            {showTakeOff && (
                                <TimePicker 
                                    label="Take-Off" 
                                    views={['hours', 'minutes']} 
                                    format="HH:mm"
                                    value={toTimeObj(flightActualDayDep, values.takeOff)}
                                    onChange={handleTimeChange('takeOff')}
                                    slotProps={{ textField: { size: 'small', sx: { flex: '1 1 130px' } } }}
                                />
                            )}
                            {showTouchDown && (
                                <TimePicker 
                                    label="Touch-Down" 
                                    views={['hours', 'minutes']} 
                                    format="HH:mm"
                                    value={toTimeObj(flightActualDayDep, values.touchDown)}
                                    onChange={handleTimeChange('touchDown')}
                                    slotProps={{ textField: { size: 'small', sx: { flex: '1 1 130px' } } }}
                                />
                            )}
                            {showETA && (
                                <TimePicker 
                                    label="ETA" 
                                    views={['hours', 'minutes']} 
                                    format="HH:mm"
                                    value={toTimeObj(flightActualDayArr, values.eta)}
                                    onChange={handleTimeChange('eta')}
                                    slotProps={{ textField: { size: 'small', sx: { flex: '1 1 130px' } } }}
                                />
                            )}
                            {showATA && (
                                <TimePicker 
                                    label="On-Block" 
                                    views={['hours', 'minutes']} 
                                    format="HH:mm"
                                    value={toTimeObj(flightActualDayArr, values.ata)}
                                    onChange={handleTimeChange('ata')}
                                    slotProps={{ textField: { size: 'small', sx: { flex: '1 1 130px' } } }}
                                />
                            )}
                        </Box>

                        {/* Delay codes (conditional) */}
                        {showDelays && (
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Autocomplete
                                        {...delayAutocompleteProps(delayCode1, (_, v) => setDelayCode1(v ? v.delayIndex : ''))}
                                        sx={{ flex: 1 }}
                                        renderInput={(params) => <TextField {...params} label="Delay Code 1" size="small" />}
                                    />
                                    <TimePicker 
                                        label="Duration 1" 
                                        views={['hours', 'minutes']}
                                        value={delayDuration1}
                                        onChange={handleDelayDur(setDelayDuration1)}
                                        slotProps={{ textField: { size: 'small', sx: { width: 140 } } }}
                                    />
                                </Box>
                                <Box display="flex" gap={1} alignItems="center">
                                    <Autocomplete
                                        {...delayAutocompleteProps(delayCode2, (_, v) => setDelayCode2(v ? v.delayIndex : ''))}
                                        sx={{ flex: 1 }}
                                        renderInput={(params) => <TextField {...params} label="Delay Code 2" size="small" />}
                                    />
                                    <TimePicker 
                                        label="Duration 2" 
                                        views={['hours', 'minutes']}
                                        value={delayDuration2}
                                        onChange={handleDelayDur(setDelayDuration2)}
                                        slotProps={{ textField: { size: 'small', sx: { width: 140 } } }}
                                    />
                                </Box>
                            </Box>
                        )}

                        {/* Pax (conditional) */}
                        {showPax && (
                            <Box 
                                display="grid" 
                                gridTemplateColumns="repeat(3, 1fr)" 
                                gap={1}
                            >
                                <TextField 
                                    type="number" 
                                    size="small" 
                                    label="Class C" 
                                    value={classC}
                                    onChange={(e) => setClassC(Math.max(0, Number(e.target.value)))}
                                    slotProps={{ input: { endAdornment: <InputAdornment position="end">C</InputAdornment> } }}
                                />
                                <TextField 
                                    type="number" 
                                    size="small" 
                                    label="Class Y" 
                                    value={classY}
                                    onChange={(e) => setClassY(Math.max(0, Number(e.target.value)))}
                                    slotProps={{ input: { endAdornment: <InputAdornment position="end">Y</InputAdornment> } }}
                                />
                                <TextField 
                                    type="number" 
                                    size="small" 
                                    label="Children" 
                                    value={classCHD}
                                    onChange={(e) => setClassCHD(Math.max(0, Number(e.target.value)))}
                                    slotProps={{ input: { endAdornment: <InputAdornment position="end">CHD</InputAdornment> } }}
                                />
                            </Box>
                        )}

                        <TextField
                            label="Supplementary Information (SI)"
                            size="small"
                            multiline
                            minRows={1}
                            value={supplementaryInfo}
                            onChange={(e) => setSupplementaryInfo(e.target.value)}
                            fullWidth
                        />

                        <Divider />

                        {/* Message preview */}
                        <Box
                            component="pre"
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.88rem',
                                lineHeight: 1.6,
                                border: `1px solid`,
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1.5,
                                bgcolor: 'action.hover',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                                m: 0,
                            }}
                        >
                            {buildMVTMessage() || '—'}
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button 
                        variant="outlined" 
                        color="info"      
                        onClick={handleCopyMessage}
                    >
                        Copy
                    </Button>
                    <Button 
                        variant="outlined" 
                        color="primary"   
                        onClick={handleSendMessage}
                    >
                        Send
                    </Button>
                    <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default MovementMessageDialog;
